import { Link } from 'react-router-dom';
import { BookOpen, Clock, Layers, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CourseHighlightProps {
  section: any;
}

function formatDuration(minutes: number): string {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

const LEVEL_COLORS: Record<string, string> = {
  beginner: 'bg-success-100 text-success-800',
  intermediate: 'bg-amber-100 text-amber-800',
  advanced: 'bg-red-100 text-red-800',
};

export default function CourseHighlight({ section }: CourseHighlightProps) {
  const courses: any[] = section.content?.courses ?? [];
  const headingId = `courses-heading-${section.id}`;

  if (courses.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="bg-cream">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {section.title && (
            <h2
              id={headingId}
              className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
            >
              {section.title}
            </h2>
          )}
          {section.subtitle && (
            <p className="mt-2 text-sage-600 max-w-xl mx-auto">
              {section.subtitle}
            </p>
          )}
          <div className="mt-4 flex items-center justify-center gap-3" aria-hidden="true">
            <div className="h-px w-12 bg-sage-300" />
            <Sparkles className="h-4 w-4 text-sage-400" />
            <div className="h-px w-12 bg-sage-300" />
          </div>
        </div>

        {/* Course grid */}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((course: any) => (
            <Link
              key={course.id ?? course.slug}
              to={`/courses/${course.slug}`}
              className="group rounded-xl bg-surface shadow-sm border border-sage-100 overflow-hidden transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
            >
              {/* Thumbnail */}
              <div className="relative aspect-video bg-sage-50 flex items-center justify-center overflow-hidden">
                {course.thumbnail_url ? (
                  <img
                    src={course.thumbnail_url}
                    alt=""
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                ) : (
                  <BookOpen className="h-12 w-12 text-sage-300" aria-hidden="true" />
                )}
                {course.level && (
                  <span
                    className={cn(
                      'absolute top-3 right-3 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize',
                      LEVEL_COLORS[course.level] ?? 'bg-sage-100 text-sage-800'
                    )}
                  >
                    {course.level}
                  </span>
                )}
              </div>

              {/* Details */}
              <div className="p-5">
                <h3 className="font-display text-lg font-semibold text-sage-900 group-hover:text-sage-700 transition-colors line-clamp-2">
                  {course.name}
                </h3>
                {course.short_description && (
                  <p className="mt-1.5 text-sm text-sage-600 line-clamp-2">
                    {course.short_description}
                  </p>
                )}

                <div className="mt-4 flex items-center gap-4 text-xs text-sage-500">
                  {course.total_lessons != null && (
                    <span className="inline-flex items-center gap-1">
                      <Layers className="h-3.5 w-3.5" aria-hidden="true" />
                      {course.total_lessons} lesson{course.total_lessons !== 1 ? 's' : ''}
                    </span>
                  )}
                  {course.total_duration_minutes != null && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                      {formatDuration(course.total_duration_minutes)}
                    </span>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between">
                  {course.price != null && (
                    <span className="font-display text-lg font-bold text-sage-900">
                      {typeof course.price === 'number'
                        ? `$${course.price.toFixed(2)}`
                        : course.price}
                    </span>
                  )}
                  <span className="text-sm font-medium text-sage-600 group-hover:text-sage-800 transition-colors">
                    View Course &rarr;
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
